import type { AnalyticsPayloadSchema } from "@Common/types/bo/analytics_public/analytics_public";
import { sendEncryptedData } from "@Src/utils/encryption";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Paper,
	Typography,
} from "@mui/material";
import { type ReactNode, useState } from "react";
import type { DataProvider } from "react-admin";

interface DisclaimerItem {
	label: string;
}

const DEFAULT_DISCLAIMERS: DisclaimerItem[] = [
	{
		label:
			"I confirm that I will not use any AI tool when answering this form.",
	},
];

interface AiDisclaimerWrapperProps {
	/** The content to render after Next is clicked with all checkboxes ticked */
	children: ReactNode;
	/** List of disclaimer checkboxes to present. Defaults to the AI disclaimer. */
	disclaimers?: DisclaimerItem[];
	dataProvider: DataProvider;
	recordId: string | null;
	publicKey: string | null;
}

export const AiDisclaimerWrapper = ({
	children,
	disclaimers = DEFAULT_DISCLAIMERS,
	dataProvider,
	recordId,
	publicKey,
}: AiDisclaimerWrapperProps) => {
	const [confirmed, setConfirmed] = useState<boolean[]>(
		disclaimers.map(() => false),
	);
	const [proceeded, setProceeded] = useState(false);

	const allConfirmed = confirmed.every(Boolean);

	if (proceeded) {
		return <Box>{children}</Box>;
	}

	return (
		<Box
			sx={{
				height: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: "#fafafa",
				p: 2,
			}}
		>
			<Paper
				elevation={3}
				sx={{
					p: 4,
					maxWidth: 650,
					textAlign: "left",
					borderRadius: 3,
				}}
			>
				<Typography variant="h5" gutterBottom textAlign="center">
					Commitment to honest answering
				</Typography>

				<Box sx={{ mb: 3 }}>
					<Typography variant="body2" color="text.secondary" paragraph>
						Before you begin, please confirm your commitment to answering this
						form honestly and with integrity, without using any external
						resources.
					</Typography>
				</Box>

				{disclaimers.map((disclaimer, index) => (
					<Box sx={{ mb: 2 }} key={disclaimer.label}>
						<FormControlLabel
							control={
								<Checkbox
									checked={confirmed[index]}
									onChange={(e) =>
										setConfirmed((prev) =>
											prev.map((v, i) => (i === index ? e.target.checked : v)),
										)
									}
								/>
							}
							label={disclaimer.label}
						/>
					</Box>
				))}

				<Box textAlign="center" sx={{ mt: 3 }}>
					<Button
						variant="contained"
						color="primary"
						size="large"
						disabled={!allConfirmed}
						onClick={() => {
							setProceeded(true);
							if (recordId && publicKey) {
								const payload: AnalyticsPayloadSchema = {
									type: "disclaimer_success",
									application_public_id: recordId,
								};
								sendEncryptedData(
									dataProvider,
									"analytics_public",
									recordId,
									payload,
									publicKey,
								).catch(() => {});
							}
						}}
					>
						Next
					</Button>
				</Box>
			</Paper>
		</Box>
	);
};
